/**
 * TicketPresencialModal — Kiosko de auto-registro estilo TypeForm.
 *
 * 4 fases:
 *   1) Datos personales: Tipo doc, N° doc, Nombre, Email.
 *   2) Proyecto + Motivo (botones grandes apilados).
 *   3) Descripción del motivo (textarea).
 *   4) Confirmación con código de ticket.
 *
 * Comportamiento:
 *   - Fullscreen overlay, touch-friendly (iPad/tablet en recepción).
 *   - Enter avanza a la siguiente fase (si validación pasa).
 *   - Auto-reset tras 15s en Fase 4 (modo kiosk).
 *   - Sin persistencia en localStorage entre clientes (privacidad).
 *   - Debounce del botón Siguiente/Confirmar.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { isCanceled } from '../../../hooks/useAbortableLoad';
import { useAuth } from '../../../context/AuthContext';
import {
  X, ArrowLeft, ArrowRight, Check, Loader2, AlertCircle, IdCard,
  Building2, Tag, MessageSquare, Sparkles, Maximize2, Minimize2, Search, Mic, MicOff,
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { Textarea } from '../../../components/ui/textarea';
import { toast } from 'sonner';
import { DEFAULT_AVISO_PRIVACIDAD } from '../avisoPrivacidad';

const TIPOS_DOC = [
  { value: 'DNI',       label: 'DNI' },
  { value: 'CE',        label: 'Carnet de Extranjería' },
  { value: 'PASAPORTE', label: 'Pasaporte' },
  { value: 'RUC',       label: 'RUC' },
];

const AUTO_RESET_MS = 15_000;
const TOTAL_STEPS = 4;

const initialState = () => ({
  cliente_tipo_documento: 'DNI',
  cliente_documento: '',
  cliente_nombre: '',
  cliente_email: '',
  cliente_celular: '+51 ',
  proyecto_id: '',
  clasificacion_l1_id: '',
  descripcion_motivo: '',
});

/** Valida formato de documento client-side (espejo del backend). */
function validarDoc(tipo, num) {
  const n = (num || '').trim();
  if (!n) return 'El número de documento es requerido.';
  if (tipo === 'DNI' && !/^\d{8}$/.test(n)) return 'El DNI debe tener 8 dígitos.';
  if (tipo === 'RUC' && !/^\d{11}$/.test(n)) return 'El RUC debe tener 11 dígitos.';
  if (tipo === 'RUC' && !/^(10|15|16|17|20)/.test(n)) return 'El RUC debe iniciar con 10, 15, 16, 17 o 20.';
  if (tipo === 'CE' && (n.length < 9 || n.length > 20)) return 'El CE debe tener entre 9 y 20 caracteres.';
  if (tipo === 'PASAPORTE' && (n.length < 6 || n.length > 20)) return 'El pasaporte debe tener entre 6 y 20 caracteres.';
  if ((tipo === 'CE' || tipo === 'PASAPORTE') && !/^[a-zA-Z0-9]+$/.test(n)) return 'Solo letras y números.';
  // Todos iguales / secuencial (heurística)
  if (new Set(n).size === 1) return 'El número no es válido.';
  return null;
}

function validarEmail(email) {
  if (!email) return 'El correo es requerido.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Correo inválido.';
  return null;
}

export default function TicketPresencialModal({ open, onClose, onCreated }) {
  const { api, empresa } = useAuth();
  const [step, setStep] = useState(1);
  const [data, setData] = useState(initialState);
  const [proyectos, setProyectos] = useState([]);
  const [motivos, setMotivos] = useState([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [resultado, setResultado] = useState(null); // { codigo, cliente_nombre }
  const [fullscreen, setFullscreen] = useState(() => {
    try { return localStorage.getItem('tk-kiosk-fullscreen') === '1'; }
    catch { return false; }
  });
  const firstInputRef = useRef(null);

  // CRM Inmobiliario ("Consultar Cliente")
  const [crmActivo, setCrmActivo] = useState(false);
  const [crmConsultado, setCrmConsultado] = useState(false);   // ya se pulsó "Ingresar"
  const [consultando, setConsultando] = useState(false);
  const [clienteProyectos, setClienteProyectos] = useState([]); // proyecto_id[] del cliente

  // Dictado por voz (Web Speech API) — Fase 3
  const [escuchando, setEscuchando] = useState(false);
  const recognitionRef = useRef(null);
  const voiceSupported = typeof window !== 'undefined' &&
    (window.SpeechRecognition || window.webkitSpeechRecognition);

  // Solo se piden Nombre/Correo manualmente si el CRM está inactivo, o ya se consultó.
  const mostrarDatosCliente = !crmActivo || crmConsultado;

  // Cargar catálogos al abrir
  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    const signal = controller.signal;
    setLoadingCatalogs(true);
    // Estado de integración CRM (define el comportamiento de la Fase 1)
    api.get('/tickets/integraciones/estado', { signal })
      .then((r) => setCrmActivo(!!r.data?.consultar_cliente_activo))
      .catch(() => { if (!signal.aborted) setCrmActivo(false); });
    Promise.all([
      api.get('/tickets/kiosk/proyectos', { signal }),
      api.get('/tickets/kiosk/motivos', { signal }),
    ]).then(([pRes, mRes]) => {
      setProyectos(pRes.data || []);
      setMotivos(mRes.data || []);
    }).catch((e) => {
      if (isCanceled(e)) return;
      toast.error('Error cargando catálogos');
      // eslint-disable-next-line no-console
      console.error(e);
    }).finally(() => { if (!signal.aborted) setLoadingCatalogs(false); });
    return () => controller.abort();
  }, [open, api]);

  // Reset cuando se cierra
  useEffect(() => {
    if (!open) {
      setStep(1);
      setData(initialState());
      setErrors({});
      setServerError('');
      setResultado(null);
      setCrmConsultado(false);
      setConsultando(false);
      setClienteProyectos([]);
      try { recognitionRef.current?.stop?.(); } catch { /* noop */ }
      setEscuchando(false);
    }
  }, [open]);

  // Autofocus en cada fase
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      try { firstInputRef.current?.focus(); } catch { /* noop */ }
    }, 250); // espera a que la transición termine
    return () => clearTimeout(t);
  }, [step, open]);

  // Auto-reset tras éxito (Fase 4) — modo kiosk
  useEffect(() => {
    if (step !== 4 || !resultado) return undefined;
    const t = setTimeout(() => {
      setStep(1);
      setData(initialState());
      setErrors({});
      setServerError('');
      setResultado(null);
    }, AUTO_RESET_MS);
    return () => clearTimeout(t);
  }, [step, resultado]);

  // Fullscreen: persistir + sincronizar con Fullscreen API del navegador.
  const toggleFullscreen = () => setFullscreen((v) => !v);

  useEffect(() => {
    if (!open) return undefined;
    try { localStorage.setItem('tk-kiosk-fullscreen', fullscreen ? '1' : '0'); }
    catch { /* noop */ }
    // Sincronizar con Fullscreen API del navegador (iPad/Safari/Chrome).
    const isInFs = !!document.fullscreenElement;
    if (fullscreen && !isInFs) {
      try { document.documentElement.requestFullscreen?.().catch(() => {}); } catch { /* noop */ }
    } else if (!fullscreen && isInFs) {
      try { document.exitFullscreen?.().catch(() => {}); } catch { /* noop */ }
    }
    return undefined;
  }, [fullscreen, open]);

  // Sincronizar state si el usuario sale de fullscreen con Esc del navegador.
  useEffect(() => {
    if (!open) return undefined;
    const onFsChange = () => {
      if (!document.fullscreenElement && fullscreen) setFullscreen(false);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, [open, fullscreen]);

  // Atajos: F11 toggle, Esc sale solo de fullscreen (sin cerrar modal).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (ev) => {
      if (ev.key === 'F11') {
        ev.preventDefault();
        toggleFullscreen();
      } else if (ev.key === 'Escape' && fullscreen) {
        ev.preventDefault();
        ev.stopPropagation();
        setFullscreen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, fullscreen]);

  // Validación por fase
  const validarFaseActual = () => {
    const e = {};
    if (step === 1) {
      const docErr = validarDoc(data.cliente_tipo_documento, data.cliente_documento);
      if (docErr) e.cliente_documento = docErr;
      const nm = (data.cliente_nombre || '').trim();
      if (nm.length < 3) e.cliente_nombre = 'Ingresa tu nombre completo.';
      else if (!nm.includes(' ')) e.cliente_nombre = 'Incluye nombre y apellido.';
      const emErr = validarEmail(data.cliente_email);
      if (emErr) e.cliente_email = emErr;
      const cel = (data.cliente_celular || '').replace(/[\s\-()]+/g, '');
      if (!cel || cel === '+') e.cliente_celular = 'El celular es obligatorio.';
      else if (!/^\+\d{1,3}\d{6,14}$/.test(cel)) e.cliente_celular = 'Incluye el código de país (ej. +51) y solo dígitos.';
    } else if (step === 2) {
      if (!data.proyecto_id) e.proyecto_id = 'Selecciona un proyecto.';
      if (!data.clasificacion_l1_id) e.clasificacion_l1_id = 'Selecciona un motivo.';
    } else if (step === 3) {
      const d = (data.descripcion_motivo || '').trim();
      if (d.length < 10) e.descripcion_motivo = 'Mínimo 10 caracteres.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = async () => {
    if (submitting) return;
    setServerError('');
    if (!validarFaseActual()) return;
    if (step < 3) {
      setStep((s) => s + 1);
    } else if (step === 3) {
      // Enviar
      setSubmitting(true);
      try {
        const { data: result } = await api.post('/tickets/kiosk/registrar', data);
        setResultado(result);
        setStep(4);
        onCreated?.();
      } catch (err) {
        const detail = err?.response?.data?.detail || 'Error al registrar el ticket.';
        setServerError(typeof detail === 'string' ? detail : 'Error al registrar.');
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleBack = () => {
    if (step > 1 && step < 4) setStep((s) => s - 1);
  };

  const handleCancel = () => {
    if (!window.confirm('¿Cancelar el registro? Se perderán los datos ingresados.')) return;
    setStep(1);
    setData(initialState());
    setErrors({});
    setServerError('');
  };

  // Enter para avanzar (excepto en textarea con Shift+Enter, y excepto en Fase 4)
  const onKeyDownShell = (ev) => {
    if (ev.key === 'Enter' && !ev.shiftKey) {
      const tag = (ev.target?.tagName || '').toLowerCase();
      if (tag === 'textarea') return; // permitir saltos de línea en descripción
      if (step === 4) return;
      ev.preventDefault();
      handleNext();
    }
  };

  // Lista de proyectos ordenada
  const proyectosSorted = useMemo(
    () => [...(proyectos || [])].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '')),
    [proyectos],
  );

  // Fase 2: la lista SIEMPRE muestra todos los proyectos (el del cliente se preselecciona).
  const proyectosDisponibles = proyectosSorted;

  // Consulta al CRM por número de documento (canal presencial)
  const consultarCliente = async () => {
    const docErr = validarDoc(data.cliente_tipo_documento, data.cliente_documento);
    if (docErr) { setErrors((e) => ({ ...e, cliente_documento: docErr })); return; }
    setErrors((e) => ({ ...e, cliente_documento: undefined }));
    setConsultando(true);
    try {
      const { data: res } = await api.post('/tickets/kiosk/consultar-cliente', {
        tipo_documento: data.cliente_tipo_documento,
        numero_documento: data.cliente_documento.trim(),
      });
      if (res.encontrado) {
        const provProyectos = (res.proyectos || []).map((p) => p.id);
        setData((d) => ({
          ...d,
          cliente_nombre: res.cliente_nombre || d.cliente_nombre,
          cliente_email: res.cliente_email || d.cliente_email,
          cliente_celular: res.cliente_celular || d.cliente_celular,
          // Preseleccionar el primer proyecto del cliente (la lista sigue mostrando todos).
          proyecto_id: provProyectos[0] || d.proyecto_id,
        }));
        setClienteProyectos(provProyectos);
        toast.success('Cliente identificado. Verifica tus datos.');
      } else {
        setClienteProyectos([]);
        toast.error('No te encontramos en nuestra base de datos. Por favor, completa todos los campos manualmente.');
      }
    } catch (e) {
      setClienteProyectos([]);
      toast.error('No te encontramos en nuestra base de datos. Por favor, completa todos los campos manualmente.');
    } finally {
      setConsultando(false);
      setCrmConsultado(true);
    }
  };

  // Dictado por voz (Web Speech API)
  const toggleDictado = () => {
    if (escuchando) {
      try { recognitionRef.current?.stop?.(); } catch { /* noop */ }
      setEscuchando(false);
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error('Tu navegador no soporta dictado por voz.'); return; }
    const rec = new SR();
    rec.lang = 'es-PE';
    rec.continuous = true;
    rec.interimResults = false;
    rec.onresult = (event) => {
      let texto = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) texto += event.results[i][0].transcript;
      }
      if (texto) {
        setData((d) => {
          const sep = d.descripcion_motivo && !/\s$/.test(d.descripcion_motivo) ? ' ' : '';
          return { ...d, descripcion_motivo: (d.descripcion_motivo + sep + texto.trim()).slice(0, 5000) };
        });
      }
    };
    rec.onerror = () => { setEscuchando(false); };
    rec.onend = () => { setEscuchando(false); };
    recognitionRef.current = rec;
    try { rec.start(); setEscuchando(true); }
    catch { setEscuchando(false); }
  };

  if (!open) return null;

  const avisoPrivacidad = (empresa?.aviso_privacidad || '').trim() || DEFAULT_AVISO_PRIVACIDAD;
  const AvisoPrivacidad = () => (
    <div className="tk-kiosk-privacy" data-testid="kiosk-aviso-privacidad">
      <span className="tk-kiosk-privacy-title">Aviso de Privacidad</span>
      <span
        className="tk-kiosk-privacy-text"
        dangerouslySetInnerHTML={{ __html: avisoPrivacidad }}
      />
    </div>
  );

  return (
    <div className="tk-kiosk-overlay" data-testid="kiosk-modal" onKeyDown={onKeyDownShell} role="dialog" aria-modal="true">
      <div className={`tk-kiosk-shell ${fullscreen ? 'is-fullscreen' : ''}`}>
        {/* Header */}
        <header className="tk-kiosk-header">
          <div className="tk-kiosk-brand">
            {empresa?.logo_url
              ? <img src={empresa.logo_url} alt={empresa?.nombre || 'Logo'} className="tk-kiosk-brand-logo" />
              : <Sparkles style={{ width: 16, height: 16 }} />}
            <span>Registro de Visita</span>
          </div>
          <div className="tk-kiosk-header-actions">
            <button
              type="button"
              className="tk-kiosk-icon-btn"
              onClick={toggleFullscreen}
              data-testid="kiosk-btn-maximize"
              aria-label={fullscreen ? 'Restaurar tamaño' : 'Pantalla completa'}
              title={fullscreen ? 'Restaurar tamaño (F11)' : 'Pantalla completa (F11)'}
            >
              {fullscreen
                ? <Minimize2 style={{ width: 18, height: 18 }} />
                : <Maximize2 style={{ width: 18, height: 18 }} />}
            </button>
            <button
              type="button"
              className="tk-kiosk-icon-btn"
              onClick={onClose}
              data-testid="kiosk-btn-close"
              aria-label="Cerrar"
            >
              <X style={{ width: 20, height: 20 }} />
            </button>
          </div>
        </header>

        {/* Progress */}
        <div className="tk-kiosk-progress" aria-label={`Paso ${step} de ${TOTAL_STEPS}`}>
          {[1, 2, 3, 4].map((s) => (
            <div
              key={s}
              className={`tk-kiosk-progress-seg ${s < step ? 'is-done' : ''} ${s === step ? 'is-current' : ''}`}
            />
          ))}
          <span className="tk-kiosk-progress-label">Paso {step} de {TOTAL_STEPS}</span>
        </div>

        {/* Stage */}
        <main className="tk-kiosk-stage">
          {loadingCatalogs ? (
            <div className="tk-kiosk-loading">
              <Loader2 className="tk-spin" style={{ width: 32, height: 32 }} />
              <span>Cargando...</span>
            </div>
          ) : (
            <>
              {step === 1 && (
                <section className="tk-kiosk-fase" data-testid="kiosk-step-1">
                  <div className="tk-kiosk-icon-bubble"><IdCard /></div>
                  <h2 className="tk-kiosk-question">¡Hola! Cuéntanos quién eres</h2>
                  <p className="tk-kiosk-helper">Necesitamos tus datos para crear tu ticket de atención.</p>

                  <div className="tk-kiosk-row">
                    <div className="tk-kiosk-field" style={{ flex: '0 0 230px' }}>
                      <label className="tk-kiosk-label">Tipo de documento</label>
                      <Select
                        value={data.cliente_tipo_documento}
                        onValueChange={(v) => setData((d) => ({ ...d, cliente_tipo_documento: v, cliente_documento: '' }))}
                      >
                        <SelectTrigger className="tk-kiosk-input" data-testid="kiosk-select-tipo-doc">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TIPOS_DOC.map((t) => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="tk-kiosk-field" style={{ flex: 1 }}>
                      <label className="tk-kiosk-label">Número de documento</label>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                        <input
                          ref={firstInputRef}
                          type="text"
                          inputMode={data.cliente_tipo_documento === 'DNI' || data.cliente_tipo_documento === 'RUC' ? 'numeric' : 'text'}
                          autoComplete="off"
                          className={`tk-kiosk-input ${errors.cliente_documento ? 'has-error' : ''}`}
                          style={{ flex: 1 }}
                          placeholder={data.cliente_tipo_documento === 'DNI' ? '12345678' : data.cliente_tipo_documento === 'RUC' ? '20123456789' : 'Número'}
                          value={data.cliente_documento}
                          onChange={(e) => setData((d) => ({ ...d, cliente_documento: e.target.value.toUpperCase() }))}
                          data-testid="kiosk-input-numero-doc"
                        />
                        {crmActivo && (
                          <button
                            type="button"
                            className="tk-kiosk-ingresar-btn"
                            onClick={consultarCliente}
                            disabled={consultando}
                            data-testid="kiosk-btn-ingresar"
                          >
                            {consultando ? <Loader2 style={{ width: 18, height: 18 }} className="tk-spin" /> : <Search style={{ width: 18, height: 18 }} />}
                            Ingresar
                          </button>
                        )}
                      </div>
                      {errors.cliente_documento && <span className="tk-kiosk-error">{errors.cliente_documento}</span>}
                    </div>
                  </div>

                  {mostrarDatosCliente && (
                  <div className="tk-kiosk-field">
                    <label className="tk-kiosk-label">Nombre completo</label>
                    <input
                      type="text"
                      autoComplete="name"
                      className={`tk-kiosk-input ${errors.cliente_nombre ? 'has-error' : ''}`}
                      placeholder="Tu nombre y apellido"
                      value={data.cliente_nombre}
                      onChange={(e) => setData((d) => ({ ...d, cliente_nombre: e.target.value }))}
                      data-testid="kiosk-input-nombre"
                    />
                    {errors.cliente_nombre && <span className="tk-kiosk-error">{errors.cliente_nombre}</span>}
                  </div>
                  )}

                  {mostrarDatosCliente && (
                  <div className="tk-kiosk-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div className="tk-kiosk-field">
                      <label className="tk-kiosk-label">Correo electrónico</label>
                      <input
                        type="email"
                        autoComplete="email"
                        inputMode="email"
                        className={`tk-kiosk-input ${errors.cliente_email ? 'has-error' : ''}`}
                        placeholder="tu@correo.com"
                        value={data.cliente_email}
                        onChange={(e) => setData((d) => ({ ...d, cliente_email: e.target.value.trim() }))}
                        data-testid="kiosk-input-email"
                      />
                      {errors.cliente_email && <span className="tk-kiosk-error">{errors.cliente_email}</span>}
                    </div>
                    <div className="tk-kiosk-field">
                      <label className="tk-kiosk-label">Celular</label>
                      <input
                        type="tel"
                        autoComplete="tel"
                        inputMode="tel"
                        className={`tk-kiosk-input ${errors.cliente_celular ? 'has-error' : ''}`}
                        placeholder="Ej. +51 999 888 777"
                        value={data.cliente_celular}
                        onChange={(e) => setData((d) => ({ ...d, cliente_celular: e.target.value }))}
                        data-testid="kiosk-input-celular"
                      />
                      {errors.cliente_celular && <span className="tk-kiosk-error">{errors.cliente_celular}</span>}
                    </div>
                  </div>
                  )}

                  {crmActivo && !crmConsultado && (
                    <p className="tk-kiosk-helper" data-testid="kiosk-crm-hint">
                      Ingresa tu número de documento y pulsa <strong>Ingresar</strong> para autocompletar tus datos.
                    </p>
                  )}

                  <AvisoPrivacidad />
                </section>
              )}

              {step === 2 && (
                <section className="tk-kiosk-fase" data-testid="kiosk-step-2">
                  <div className="tk-kiosk-icon-bubble"><Building2 /></div>
                  <h2 className="tk-kiosk-question">¿Por qué proyecto nos visitas?</h2>

                  <div className="tk-kiosk-field">
                    <label className="tk-kiosk-label">Proyecto</label>
                    <Select
                      value={data.proyecto_id}
                      onValueChange={(v) => setData((d) => ({ ...d, proyecto_id: v }))}
                    >
                      <SelectTrigger
                        ref={firstInputRef}
                        className={`tk-kiosk-input ${errors.proyecto_id ? 'has-error' : ''}`}
                        data-testid="kiosk-select-proyecto"
                      >
                        <SelectValue placeholder="Selecciona un proyecto" />
                      </SelectTrigger>
                      <SelectContent>
                        {proyectosDisponibles.map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.proyecto_id && <span className="tk-kiosk-error">{errors.proyecto_id}</span>}
                  </div>

                  <div className="tk-kiosk-field">
                    <label className="tk-kiosk-label tk-kiosk-label-lg">
                      <Tag style={{ width: 16, height: 16, marginRight: 6, verticalAlign: '-3px' }} />
                      ¿Cuál es el motivo de tu visita?
                    </label>
                    <div className="tk-kiosk-motivos">
                      {motivos.length === 0 ? (
                        <p className="tk-kiosk-helper">No hay motivos configurados. Contacta a tu administrador.</p>
                      ) : motivos.map((m) => {
                        const sel = data.clasificacion_l1_id === m.clasificacion_id;
                        return (
                          <button
                            key={m.clasificacion_id}
                            type="button"
                            className={`tk-kiosk-motivo-btn ${sel ? 'is-selected' : ''}`}
                            onClick={() => setData((d) => ({ ...d, clasificacion_l1_id: m.clasificacion_id }))}
                            data-testid={`kiosk-motivo-btn-${m.clasificacion_id}`}
                          >
                            <span className="tk-kiosk-motivo-name">{m.nombre}</span>
                            {sel && <Check className="tk-kiosk-motivo-check" style={{ width: 18, height: 18 }} />}
                          </button>
                        );
                      })}
                    </div>
                    {errors.clasificacion_l1_id && <span className="tk-kiosk-error">{errors.clasificacion_l1_id}</span>}
                  </div>
                </section>
              )}

              {step === 3 && (
                <section className="tk-kiosk-fase" data-testid="kiosk-step-3">
                  <div className="tk-kiosk-icon-bubble"><MessageSquare /></div>
                  <h2 className="tk-kiosk-question">Cuéntanos un poco más</h2>
                  <p className="tk-kiosk-helper">Describe con tus propias palabras el motivo de tu visita <strong>(obligatorio)</strong>. Con este detalle clasificamos automáticamente tu caso para atenderte mejor.</p>

                  <div className="tk-kiosk-field">
                    <div style={{ position: 'relative' }}>
                      <Textarea
                        ref={firstInputRef}
                        className={`tk-kiosk-input tk-kiosk-textarea ${errors.descripcion_motivo ? 'has-error' : ''}`}
                        placeholder="Ej: Necesito una copia digital de mi contrato firmado..."
                        rows={6}
                        maxLength={5000}
                        value={data.descripcion_motivo}
                        onChange={(e) => setData((d) => ({ ...d, descripcion_motivo: e.target.value }))}
                        data-testid="kiosk-textarea-descripcion"
                      />
                      {voiceSupported && (
                        <button
                          type="button"
                          className={`tk-kiosk-mic-btn ${escuchando ? 'is-recording' : ''}`}
                          onClick={toggleDictado}
                          title={escuchando ? 'Detener dictado' : 'Dictar por voz'}
                          data-testid="kiosk-btn-voz"
                        >
                          {escuchando ? <MicOff style={{ width: 20, height: 20 }} /> : <Mic style={{ width: 20, height: 20 }} />}
                        </button>
                      )}
                    </div>
                    {escuchando && (
                      <span className="tk-kiosk-helper" data-testid="kiosk-voz-estado" style={{ color: '#dc2626' }}>
                        🎙️ Escuchando… habla ahora. Pulsa el micrófono para detener.
                      </span>
                    )}
                    <div className="tk-kiosk-counter" data-testid="kiosk-desc-counter">
                      {(data.descripcion_motivo || '').length} / 5000
                    </div>
                    {errors.descripcion_motivo && <span className="tk-kiosk-error">{errors.descripcion_motivo}</span>}
                  </div>

                  <AvisoPrivacidad />
                </section>
              )}

              {step === 4 && resultado && (
                <section className="tk-kiosk-fase tk-kiosk-fase-success" data-testid="kiosk-step-4">
                  <div className="tk-kiosk-check-circle">
                    <Check style={{ width: 56, height: 56 }} />
                  </div>
                  <h2 className="tk-kiosk-success-title">¡Gracias, {resultado.cliente_nombre.split(' ')[0]}!</h2>
                  <p className="tk-kiosk-success-text">Tu ticket ha sido registrado.</p>
                  <div className="tk-kiosk-codigo" data-testid="kiosk-success-codigo">
                    <span className="tk-kiosk-codigo-label">Número de ticket</span>
                    <span className="tk-kiosk-codigo-value">{resultado.codigo}</span>
                  </div>
                  <p className="tk-kiosk-success-text">En breve serás atendido por un especialista.</p>
                  <p className="tk-kiosk-success-mini">Te enviamos una copia a tu correo.</p>
                </section>
              )}

              {serverError && (
                <div className="tk-kiosk-server-error" role="alert">
                  <AlertCircle style={{ width: 16, height: 16, flexShrink: 0 }} />
                  <span>{serverError}</span>
                </div>
              )}
            </>
          )}
        </main>

        {/* Footer */}
        {step < 4 && !loadingCatalogs && (
          <footer className="tk-kiosk-footer">
            <button
              type="button"
              className="tk-kiosk-btn-secondary"
              onClick={handleBack}
              disabled={step === 1 || submitting}
              data-testid="kiosk-btn-back"
            >
              <ArrowLeft style={{ width: 16, height: 16 }} /> Atrás
            </button>

            <button
              type="button"
              className="tk-kiosk-btn-cancel"
              onClick={handleCancel}
              disabled={submitting}
              data-testid="kiosk-btn-cancel"
            >
              Cancelar
            </button>

            <button
              type="button"
              className="tk-kiosk-btn-primary"
              onClick={handleNext}
              disabled={submitting}
              data-testid="kiosk-btn-next"
            >
              {submitting ? (
                <><Loader2 className="tk-spin" style={{ width: 16, height: 16 }} /> Enviando...</>
              ) : step === 3 ? (
                <>Confirmar <Check style={{ width: 16, height: 16 }} /></>
              ) : (
                <>Siguiente <ArrowRight style={{ width: 16, height: 16 }} /></>
              )}
            </button>
          </footer>
        )}
      </div>
    </div>
  );
}
